"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { BrandContent } from "../brand";
import { BreadcrumbsSlot } from "../breadcrumbs";
import { ChevronIcon } from "../chevron-icon";
import { PracticeToolIcon } from "../practice-tool-icon";
import { AppLauncher } from "./app-launcher";
import { PlayerAvatarContent } from "./avatar";
import { PlayerIcon } from "./icon";
import { usePlayerSession } from "./session";

const tabs = {
  practice: {
    id: "practice",
    icon: <PlayerIcon name="practice" />,
    label: "練習",
    href: "/practice",
    matches: /^\/(practice|drills|lessons|weaknesses|count-nine|bowlard)(\/|$)/,
  },
  tools: {
    id: "tools",
    icon: <PracticeToolIcon name="tools" size={22} />,
    label: "ツール",
    href: "/tools",
    matches:
      /^\/(tools|layouts|cue-tip|set-match|scoreboards|break-speed)(\/|$)/,
  },
  shop: {
    id: "shop",
    icon: <PracticeToolIcon name="shop" size={22} />,
    label: "ストア",
    href: "/shop",
    matches: /^\/shop(\/|$)/,
  },
  recruitments: {
    id: "people",
    icon: <PracticeToolIcon name="people" size={22} />,
    label: "募集",
    href: "/recruitments",
    matches: /^\/recruitments(\/|$)/,
  },
  home: {
    id: "home",
    icon: <PlayerIcon name="home" />,
    label: "ホーム",
    href: "/",
    matches: /^\/$/,
  },
  events: {
    id: "events",
    icon: <PracticeToolIcon name="events" size={22} />,
    label: "イベント",
    href: "/events",
    matches: /^\/events(\/|$)/,
  },
  stores: {
    id: "stores",
    icon: <PracticeToolIcon name="stores" size={22} />,
    label: "施設",
    href: "/stores",
    matches: /^\/stores(\/|$)/,
  },
  players: {
    id: "profile",
    icon: <PracticeToolIcon name="players" size={22} />,
    label: "プレイヤー",
    href: "/players",
    matches: /^\/players(\/|$)/,
  },
} as const;

const mobileTabs = [
  tabs.home,
  tabs.shop,
  tabs.practice,
  tabs.stores,
  tabs.events,
];
const sidebarTabs = [
  tabs.home,
  tabs.practice,
  tabs.tools,
  tabs.shop,
  tabs.events,
  tabs.stores,
  tabs.recruitments,
  tabs.players,
];

function PlayerBrand({
  focused = false,
  className = "player-brand",
}: {
  focused?: boolean;
  className?: string;
}) {
  return (
    <Link
      href="/"
      className={`${className}${focused ? " player-header-brand-focused" : ""}`}
      aria-label="タマリバ ホーム"
    >
      <BrandContent />
    </Link>
  );
}

export function PlayerNavigation({
  hideDesktopNavigation = false,
  hideMobileNavigation = false,
}: {
  hideDesktopNavigation?: boolean;
  hideMobileNavigation?: boolean;
}) {
  const pathname = usePathname();
  const session = usePlayerSession();
  const mobileOnly =
    hideDesktopNavigation || session.focus?.hideDesktopNavigation;
  return (
    <div
      className={`${mobileOnly ? "player-navigation-mobile-only" : "player-navigation"}${session.focused ? " player-navigation-focused" : ""}${hideMobileNavigation ? " player-navigation-hide-mobile" : ""}`}
    >
      <nav className="player-tabs" aria-label="プレイヤーのメインメニュー">
        {mobileTabs.map((tab) => (
          <Link
            key={tab.id}
            className={`player-tab player-tab-${tab.id}`}
            href={tab.href}
            aria-current={tab.matches.test(pathname) ? "page" : undefined}
          >
            <span className="player-tab-content">
              <span className="player-tab-icon">{tab.icon}</span>
              <span>{tab.label}</span>
            </span>
          </Link>
        ))}
      </nav>
      <aside className="player-sidebar">
        <div className="player-sidebar-brand">
          <PlayerBrand />
        </div>
        <nav
          className="player-sidebar-menu"
          aria-label="プレイヤーのメインメニュー"
        >
          {sidebarTabs.map((tab) => (
            <Link
              key={tab.id}
              className="player-side-link"
              href={tab.href}
              aria-current={tab.matches.test(pathname) ? "page" : undefined}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </Link>
          ))}
        </nav>
      </aside>
    </div>
  );
}

export function PlayerHeaderBrand({
  guest = false,
  mobileBackHref,
}: {
  guest?: boolean;
  mobileBackHref?: string;
}) {
  const { focus } = usePlayerSession();
  return (
    <>
      {focus?.onExit && (
        <button
          className="player-exit"
          type="button"
          onClick={focus.onExit}
          disabled={focus.exitDisabled}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
          終了する
        </button>
      )}
      {!focus?.onExit && mobileBackHref && (
        <Link href={mobileBackHref} className="player-exit">
          <ChevronIcon direction="left" size={20} />
          戻る
        </Link>
      )}
      <PlayerBrand
        focused={!!focus?.onExit || !!mobileBackHref}
        className={guest ? "brand" : "player-brand"}
      />
    </>
  );
}

export function PlayerHeader({
  hideDesktopNavigation = false,
  mobileBackHref,
  hideMobileNavigation = false,
}: {
  hideDesktopNavigation?: boolean;
  mobileBackHref?: string;
  hideMobileNavigation?: boolean;
}) {
  const { player } = usePlayerSession();
  const pathname = usePathname();
  return (
    <>
      <header className="player-header">
        <a className="skip-link" href="#main-content">
          本文へ移動
        </a>
        <BreadcrumbsSlot />
        <PlayerHeaderBrand mobileBackHref={mobileBackHref} />
        <nav className="player-header-actions" aria-label="アカウントメニュー">
          <Link
            href="/notifications"
            className="player-header-icon"
            aria-label="お知らせ"
            aria-current={pathname === "/notifications" ? "page" : undefined}
          >
            <PlayerIcon name="bell" />
          </Link>
          <AppLauncher />
          <Link
            href="/account"
            className="player-avatar"
            aria-label={`${player?.displayName ?? "自分"}のプロフィール`}
            aria-current={
              pathname === "/account" || pathname.startsWith("/account/")
                ? "page"
                : undefined
            }
          >
            <span aria-hidden="true">
              <PlayerAvatarContent
                displayName={player?.displayName}
                avatarUrl={player?.avatarUrl}
              />
            </span>
            <span className="sr-only">プロフィールを開く</span>
          </Link>
        </nav>
      </header>
      <PlayerNavigation
        hideDesktopNavigation={hideDesktopNavigation}
        hideMobileNavigation={hideMobileNavigation}
      />
    </>
  );
}
